import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDataProvider, useGetList, type Identifier } from "ra-core";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Building2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Sale } from "../types";

export interface CompanyDuplicateRadarProps {
  currentCompanyId?: Identifier;
  onDuplicateChange?: (isDuplicate: boolean, duplicateCompany?: any) => void;
}

export const CompanyDuplicateRadar = ({
  currentCompanyId,
  onDuplicateChange,
}: CompanyDuplicateRadarProps) => {
  const { control } = useFormContext();
  const name = useWatch({ control, name: "name" }) || "";
  const website = useWatch({ control, name: "website" }) || "";
  const dataProvider = useDataProvider();
  const navigate = useNavigate();

  const { data: salesList } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
  });

  const [matchedCompany, setMatchedCompany] = useState<any | null>(null);
  const [matchReason, setMatchReason] = useState<string>("");

  useEffect(() => {
    const trimmedName = name.trim();
    const cleanWebsite = website.trim().replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/.*$/, "");

    if (!trimmedName && !cleanWebsite) {
      setMatchedCompany(null);
      setMatchReason("");
      onDuplicateChange?.(false, null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        let found: any = null;
        let reason = "";

        // 1. Check exact or close name match
        if (trimmedName.length >= 2) {
          const res = await dataProvider.getList("companies_summary", {
            filter: { "name@ilike": trimmedName },
            pagination: { page: 1, perPage: 10 },
            sort: { field: "id", order: "ASC" },
          });

          const nameMatch = res.data.find(
            (c) =>
              c.id !== currentCompanyId &&
              c.name.trim().toLowerCase() === trimmedName.toLowerCase(),
          );

          if (nameMatch) {
            found = nameMatch;
            reason = "A company with this exact name already exists in CRM.";
          }
        }

        // 2. Check website domain match if no name match
        if (!found && cleanWebsite.length >= 3) {
          const res = await dataProvider.getList("companies_summary", {
            filter: { "website@ilike": `%${cleanWebsite}%` },
            pagination: { page: 1, perPage: 10 },
            sort: { field: "id", order: "ASC" },
          });

          const webMatch = res.data.find((c) => {
            if (c.id === currentCompanyId || !c.website) return false;
            const existingDomain = c.website
              .trim()
              .replace(/^https?:\/\//i, "")
              .replace(/^www\./i, "")
              .replace(/\/.*$/, "");
            return existingDomain.toLowerCase() === cleanWebsite.toLowerCase();
          });

          if (webMatch) {
            found = webMatch;
            reason = `A company with domain "${cleanWebsite}" already exists.`;
          }
        }

        setMatchedCompany(found);
        setMatchReason(reason);
        onDuplicateChange?.(Boolean(found), found);
      } catch (err) {
        console.error("Duplicate check error:", err);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [name, website, currentCompanyId, dataProvider, onDuplicateChange]);

  if (!matchedCompany) return null;

  const owner = salesList?.find((s) => s.id === matchedCompany.sales_id);
  const ownerName = owner ? `${owner.first_name} ${owner.last_name}` : "Unassigned";

  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 backdrop-blur-xs p-4 my-3 text-sm text-amber-950 dark:text-amber-100 shadow-sm animate-in fade-in slide-in-from-top-1 duration-200">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-amber-500/20 text-amber-600 dark:text-amber-400 shrink-0">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-base tracking-tight">
              Duplicate Company Detected
            </h4>
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300">
              Already in CRM
            </span>
          </div>

          <p className="text-xs text-amber-800 dark:text-amber-200/80 mt-0.5">
            {matchReason}
          </p>

          <div className="mt-2.5 p-2.5 rounded-lg bg-white/70 dark:bg-zinc-900/70 border border-amber-300/40 dark:border-amber-700/40 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div>
              <span className="text-muted-foreground block text-[11px]">Company Name</span>
              <span className="font-semibold flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                {matchedCompany.name}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground block text-[11px]">Account Owner</span>
              <span className="font-medium text-foreground">{ownerName}</span>
            </div>
            <div>
              <span className="text-muted-foreground block text-[11px]">Existing Activity</span>
              <span className="font-medium text-foreground">
                {matchedCompany.nb_contacts || 0} contacts • {matchedCompany.nb_deals || 0} deals
              </span>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="default"
              className="bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-500 dark:hover:bg-amber-600 font-medium h-8 text-xs gap-1.5 shadow-sm"
              onClick={() => navigate(`/companies/${matchedCompany.id}/show`)}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open Existing Company Record
            </Button>
            <span className="text-xs text-muted-foreground italic">
              Please avoid adding duplicates so sales data stays unified.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
